import Bugsnag from '@bugsnag/js';
import { DesktopWrapper } from 'components/calendar/BookingCalendarWrapper';
import CalendarInput from 'components/calendar/CalendarInput';
import {
  bookingDurationAtom,
  bookingTripTimeAtom,
  isBookingMultiDayAtom,
  selectedBookingPackageAtom,
} from 'components/forms/bookingWidget/jotaiStore';
import {
  apiParamsHourNameMapReverse,
  createDurationOption,
  createMultiDayDurationOption,
} from 'components/forms/bookingWidget/utils';
import { isDurationAvailable } from 'components/forms/TimePicker/utils';
import dayjs from 'dayjs';
import { useAtom, useAtomValue } from 'jotai';
import { useRouter } from 'next/router';
import { type UseFormMethods } from 'react-hook-form';
import styled, { css } from 'styled-components';
import { Boat, BoatPackage, DomesticV2BoatsIdCalendarGet200Response } from 'swagger/models';
import { EMPTY } from 'utils/atomUtils';
import { InputWrap, SimplifiedRHFErrors } from './shared';

interface MultiDayDuration {
  startDate: Date;
  endDate: Date;
}

const getStartAndEndDates = (newDate: Date | MultiDayDuration, isBookingMultiDay: boolean) => {
  // These two values were implicitly associated before refactoring. This is here to ensure this helper doesn't have a
  // false assumption.
  if (isBookingMultiDay && newDate instanceof Date) {
    Bugsnag.notify('DateInput: date type mismatch on multi day - expected MultiDayDuration, got Date');
  }

  let startDate: string;
  let endDate: string | undefined;
  if (newDate instanceof Date) {
    startDate = dayjs(newDate).format('YYYY-MM-DD');
    endDate = undefined;
  } else {
    startDate = dayjs(newDate.startDate).format('YYYY-MM-DD');
    endDate = dayjs(newDate.endDate).format('YYYY-MM-DD');
  }

  return { startDate, endDate };
};

interface DateInputProps {
  isModal: boolean;
  isMessageOwner: boolean;
  smallScreen: boolean;

  // errors here is a simplified interface for react-hook-form errors from the parent component.
  errors?: SimplifiedRHFErrors;
  register: UseFormMethods['register'];

  boatDetails: Boat;
  primaryPackage: BoatPackage;
  boatCalendar: DomesticV2BoatsIdCalendarGet200Response;
}

export default ({
  isModal,
  isMessageOwner,
  smallScreen,
  errors,
  register,
  boatDetails,
  primaryPackage,
  boatCalendar,
}: DateInputProps) => {
  const router = useRouter();
  const isBookingMultiDay = useAtomValue(isBookingMultiDayAtom);
  const [bookingDuration, setBookingDuration] = useAtom(bookingDurationAtom);
  const [selectedBookingPackage, setSelectedBookingPackage] = useAtom(selectedBookingPackageAtom);
  const [tripTime, setTripTime] = useAtom(bookingTripTimeAtom);

  return (
    <InputWrap noBorder={isModal}>
      <StyledCalendarInput
        isMessageOwner={isMessageOwner}
        label="Date"
        monthsToShow={!smallScreen && isMessageOwner ? 1 : 2}
        modalVersion={isModal}
        hidePrice
        hideMultiDay={!primaryPackage?.all_day_cents}
        error={errors?.trip_start?.message}
        // onMultiDayChange triggers when toggling between single day and multi day, not when a multiday selection has
        // been made. `currentDate` is the current selected date. `isMultiDaySelected` reflects the updated state of the
        // multi-day checkbox, i.e. "is the toggle now multiday?"
        onMultiDayChange={(currentDate: Date, isMultiDaySelected: boolean) => {
          if (!isMultiDaySelected) {
            setBookingDuration(
              createDurationOption(
                apiParamsHourNameMapReverse[boatDetails.cheapest_package.price_name],
                boatDetails.cheapest_package.price_name
              )
            );
            setSelectedBookingPackage(boatDetails.packages.find((p) => p.id === boatDetails.cheapest_package.id));
          } else {
            setBookingDuration(createMultiDayDurationOption(currentDate, currentDate));
          }
        }}
        // onChange
        onChange={(newDate: Date | MultiDayDuration) => {
          const { startDate, endDate } = getStartAndEndDates(newDate, isBookingMultiDay);
          router.push(
            {
              query: {
                ...router.query,
                start_period: startDate,
                end_period: endDate,
              },
            },
            undefined,
            { shallow: true, scroll: false }
          );

          if (isBookingMultiDay) return;

          if (
            !isDurationAvailable(
              startDate,
              boatCalendar,
              selectedBookingPackage?.package_type,
              tripTime?.value,
              bookingDuration?.value
            )
          ) {
            setBookingDuration(EMPTY);
            setTripTime(EMPTY);
          }
        }}
        hasRelativePositionedDate
      />

      <input type="hidden" ref={register({ required: 'Please enter your trip date' })} name="trip_start" />
    </InputWrap>
  );
};

const StyledCalendarInput = styled(CalendarInput)`
  ${DesktopWrapper} {
    right: 0;
    transform: translateY(-50px);
  }

  ${({ monthsToShow }) =>
    monthsToShow === 1 &&
    css`
      ${DesktopWrapper} {
        transform: translateY(1px) translateX(50%);
        right: 50%;
        //right: initial;
      }
    `}
`;
